import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Zap } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the correct GST invoice format in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A GST invoice must include: supplier name, address, GSTIN; invoice number and date; recipient details and GSTIN (if registered); place of supply; HSN/SAC code for each item; taxable value; applicable GST rate; CGST, SGST or IGST amount; and the total invoice value. Invilic generates all of this automatically.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is a GST invoice template free to download?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Using Invilic, you can create a filled, ready-to-send GST invoice and download it as a professional PDF for free, up to 5 invoices per month. No blank template to fill in Excel or Word, just enter your details and download.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the GST invoice number format?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'GST invoice numbers must be sequential and unique within a financial year. A common format is INV-2024-001, or you can use your own system. The number cannot exceed 16 characters.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I use a GST invoice template in Word or Excel?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can, but it is error-prone, you have to manually calculate CGST, SGST, and IGST. Invilic is better: enter your details once, get a professional, calculation-accurate PDF instantly for free.',
      },
    },
  ],
};

export default function GstInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="GST Invoice Template: Free Download & Online Generator | Invilic"
        description="Free GST invoice template for India. Auto-calculates CGST, SGST and IGST. Fill in online and download a professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/gst-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template · India
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            GST Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Skip the blank Word or Excel template. Create a filled, professional GST invoice online in 2 minutes and download a perfectly formatted PDF for Free. Auto-calculates CGST, SGST, and IGST.
          </p>
          <Link
            to="/create/gst"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg"
          >
            Use Free GST Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Better than Word/Excel, no manual calculations</p>
        </div>

        {/* Why online beats a static template */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Why Invilic is Better Than a Static GST Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Zap, title: 'Auto tax calculation', desc: 'Enter the GST rate and CGST, SGST, and IGST are calculated instantly. No formulas to set up in Excel.' },
              { icon: CheckCircle, title: 'GST-compliant format', desc: 'Every required GST field is included: GSTIN, HSN/SAC, place of supply, tax breakdown, and invoice number.' },
              { icon: Download, title: 'Professional PDF output', desc: 'Download a clean, professional PDF, not a spreadsheet printout. Clients take it seriously.' },
              { icon: ArrowRight, title: 'Reusable across clients', desc: 'Your business details are saved. Creating the next invoice takes under 60 seconds.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* GST invoice format fields */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            GST Invoice Format: Required Fields
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Under the CGST Act, 2017, a tax invoice must contain the following information. Invilic's GST template includes all of these fields:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Supplier name, address & GSTIN',
              'Invoice number (max 16 characters, sequential)',
              'Invoice date',
              'Place of supply (state)',
              'Recipient name, address & GSTIN',
              'HSN/SAC code per line item',
              'Description of goods or services',
              'Quantity and unit',
              'Taxable value',
              'GST rate (CGST + SGST or IGST)',
              'Tax amount per item',
              'Total invoice value',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-3.5 rounded-xl border border-emerald-100 dark:border-emerald-800/30 bg-emerald-50 dark:bg-emerald-900/10">
                <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            GST Invoice Template: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your GST Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Fill in your details, download a professional PDF. No Excel required.</p>
          <Link
            to="/create/gst"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200 text-lg"
          >
            Use GST Invoice Template <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

